import io from 'socket.io-client';
import ApiStorage from '../redux/ApiStorage';
import ClientSaveActions from '../actions/ClientSaveActions';

const socket = io(process.env.REACT_APP_SOCKET_URL, {
  autoConnect: false,
  forceNew: false,
  reconnection: true,
  withCredentials: true,
});

const LOCATION_UPDATE_EVENT = 'location:update';
const LOCATION_SUBSCRIBE_EVENT = 'location:subscribe';
const LOCATION_UNSUBSCRIBE_EVENT = 'location:unsubscribe';
const LOCATION_STALE_AFTER_MS = 10 * 60 * 1000;
const LOCATION_ACK_TIMEOUT_MS = 10000;

let activeClientId = null;
let socketReady = false;
const activeLocationSubscriptions = new Set();
const locationUpdateListeners = new Set();

const handleClientMessage = (res) => {
  console.warn('UtilSocket.onClientChannel :>> ', res);
  switch (res.type) {
    case 'badgeCountOrderPayment':
      ApiStorage.setBadgeCountOrder(res.data.badgeCountOrder);
      ApiStorage.setBadgeCountPayment(res.data.badgeCountPayment);
      break;
    case 'badgeCountCustomer':
      ApiStorage.setBadgeCountCustomer(res.data.badgeCountCustomer);
      break;
    case 'stepDone':
      ApiStorage.setCurrentUser(
        Object.assign({}, ApiStorage.getCurrentUser(), res.data)
      );
      break;
    default:
      break;
  }
};

const normalizeId = (value) => {
  const id = Number(value);
  return Number.isSafeInteger(id) && id > 0 ? id : null;
};

const getLocationFreshness = (capturedAt) => {
  const capturedAtMs = new Date(capturedAt).getTime();
  if (Number.isNaN(capturedAtMs)) return 'lastSeen';
  return Date.now() - capturedAtMs > LOCATION_STALE_AFTER_MS
    ? 'lastSeen'
    : 'live';
};

const handleLocationUpdate = (location) => {
  const nextLocation = {
    ...location,
    locationState: getLocationFreshness(location?.capturedAt),
  };

  console.log('Socket driver location update:', nextLocation);
  locationUpdateListeners.forEach((listener) => {
    try {
      listener(nextLocation);
    } catch (error) {
      console.warn('Driver location listener failed:', error);
    }
  });
};

const getLocationTarget = (idUser) => {
  const normalizedClientId = normalizeId(activeClientId);
  const normalizedUserId = normalizeId(idUser);
  if (!normalizedClientId || !normalizedUserId) return null;

  return {
    idClient: normalizedClientId,
    idUser: normalizedUserId,
  };
};

const emitLocationEvent = (eventName, target) => {
  if (!socket.connected || !socketReady) {
    return Promise.resolve({
      success: true,
      idUser: target.idUser,
      queued: eventName === LOCATION_SUBSCRIBE_EVENT,
      driverOnline: false,
      requested: false,
    });
  }

  return new Promise((resolve) => {
    socket
      .timeout(LOCATION_ACK_TIMEOUT_MS)
      .emit(eventName, target, (error, response) => {
        if (error) {
          resolve({
            success: false,
            idUser: target.idUser,
            error: 'ACK_TIMEOUT',
          });
          return;
        }
        resolve(response);
      });
  });
};

const emitLocationSubscription = async (idUser) => {
  const target = getLocationTarget(idUser);
  if (!target) {
    return { success: false, idUser, error: 'INVALID_TARGET' };
  }

  const result = await emitLocationEvent(LOCATION_SUBSCRIBE_EVENT, target);
  if (!result.success && result.error !== 'ACK_TIMEOUT') {
    activeLocationSubscriptions.delete(target.idUser);
  }
  return result;
};

const initializeClientSocket = () => {
  if (activeClientId) {
    ClientSaveActions.initSocket();
  }

  activeLocationSubscriptions.forEach((idUser) => {
    emitLocationSubscription(idUser).then((result) => {
      if (!result.success) {
        console.warn('Driver location subscription failed:', result);
      }
    });
  });
};

socket.on('client:ready', () => {
  socketReady = true;
  initializeClientSocket();
});
socket.on('disconnect', () => {
  socketReady = false;
});
socket.on(LOCATION_UPDATE_EVENT, handleLocationUpdate);

const UtilSocket = {
  startClientChannel: (idClient) => {
    const token = ApiStorage.getAccessToken();
    const normalizedClientId = normalizeId(idClient);
    if (!normalizedClientId || !token) return;

    const clientChanged =
      activeClientId && activeClientId !== normalizedClientId;
    if (clientChanged) {
      socket.off(activeClientId, handleClientMessage);
      activeLocationSubscriptions.clear();
      socketReady = false;
      if (socket.connected) {
        socket.disconnect();
      }
    }

    activeClientId = normalizedClientId;
    socket.off(normalizedClientId, handleClientMessage);
    socket.on(normalizedClientId, handleClientMessage);
    socket.auth = { token };

    if (socket.connected && socketReady) {
      initializeClientSocket();
    } else {
      socket.connect();
    }
  },
  subscribeDriverLocation: async (idUser) => {
    const normalizedUserId = normalizeId(idUser);
    if (!normalizedUserId || !normalizeId(activeClientId)) {
      return { success: false, idUser, error: 'INVALID_TARGET' };
    }

    activeLocationSubscriptions.add(normalizedUserId);
    return await emitLocationSubscription(normalizedUserId);
  },
  subscribeDriverLocations: async (idUsers) => {
    if (!Array.isArray(idUsers)) {
      return [{ success: false, error: 'INVALID_TARGETS' }];
    }

    return await Promise.all(
      idUsers.map((idUser) => UtilSocket.subscribeDriverLocation(idUser))
    );
  },
  unsubscribeDriverLocation: async (idUser) => {
    const target = getLocationTarget(idUser);
    if (!target) {
      return { success: false, idUser, error: 'INVALID_TARGET' };
    }

    activeLocationSubscriptions.delete(target.idUser);
    return await emitLocationEvent(LOCATION_UNSUBSCRIBE_EVENT, target);
  },
  unsubscribeDriverLocations: async (idUsers) => {
    if (!Array.isArray(idUsers)) {
      return [{ success: false, error: 'INVALID_TARGETS' }];
    }

    return await Promise.all(
      idUsers.map((idUser) => UtilSocket.unsubscribeDriverLocation(idUser))
    );
  },
  unsubscribeAllDriverLocations: async () => {
    const ids = [...activeLocationSubscriptions];
    return await Promise.all(
      ids.map((idUser) => UtilSocket.unsubscribeDriverLocation(idUser))
    );
  },
  onDriverLocation: (listener) => {
    if (typeof listener !== 'function') return () => {};

    locationUpdateListeners.add(listener);
    return () => locationUpdateListeners.delete(listener);
  },
  getLocationFreshness,
  closeSocket: () => {
    try {
      if (activeClientId) {
        socket.off(activeClientId, handleClientMessage);
      }
      activeLocationSubscriptions.clear();
      activeClientId = null;
      socketReady = false;
      socket.auth = {};
      socket.disconnect();
    } catch (error) {
      console.warn('error :>> ', error);
    }
  },
};

export default UtilSocket;
